import React, { useEffect, useState } from 'react';
import { StatusBar } from '@capacitor/status-bar';
import type { SafeAreaInsets } from './types';
import { logger } from '../../utils/logger';
// Import capacitor utilities
import { isCapacitorAvailable } from '../../capacitor-utils';

// Context to provide safe area insets to any component that needs them
export const SafeAreaContext = React.createContext<SafeAreaInsets>({
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});

interface CapacitorSafeAreaProviderProps {
  children: React.ReactNode;
}

/**
 * This component provides safe area inset information but doesn't apply global styles
 * It follows our new approach of letting individual components handle their own
 * status bar and safe area adjustments
 */
export function CapacitorSafeAreaProvider({ children }: CapacitorSafeAreaProviderProps) {
  // Initialize with default insets
  const [safeAreaInsets, setSafeAreaInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  // Check if we're in a Capacitor app
  const isNativeApp = isCapacitorAvailable();

  // Fetch and provide safe area insets but don't apply global styles
  useEffect(() => {
    if (!isNativeApp) {
      logger.capacitor('Not in native app, skipping safe area insets');
      return;
    }

    logger.capacitor('Initializing safe area insets');

    // Function to get status bar height and safe area information
    const updateSafeAreaInsets = async () => {
      try {
        // Default insets start at 0
        const insets: SafeAreaInsets = { top: 0, bottom: 0, left: 0, right: 0 };

        // Get platform safely
        let platform = 'web';
        try {
          if (window.Capacitor?.getPlatform) {
            platform = window.Capacitor.getPlatform();
          }
        } catch (err) {
          logger.error('Error getting platform', err);
        }

        // For Android: Get status bar height
        if (platform === 'android') {
          logger.capacitor('Getting Android status bar height');
          try {
            const statusBarInfo = await StatusBar.getInfo();
            insets.top = statusBarInfo?.height || 0;
            logger.capacitor('Android status bar height', { height: insets.top });
          } catch (err) {
            logger.error('Error getting status bar info', err);
            insets.top = 24; // Fallback value for Android
          }
        } else if (platform === 'ios') {
          logger.capacitor('iOS platform detected - will use CSS env variables');
          insets.top = 47; // Default value for iOS status bar
          insets.bottom = 34; // Default value for iOS home indicator area
        }

        setSafeAreaInsets(insets);
        logger.capacitor('Safe area insets updated', { insets });
      } catch (error) {
        logger.error('Error getting safe area insets', error);
      }
    };

    updateSafeAreaInsets();
  }, [isNativeApp]);

  // Simply provide the context with inset values
  return <SafeAreaContext.Provider value={safeAreaInsets}>{children}</SafeAreaContext.Provider>;
}
